import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Auth } from 'firebase/auth';
import { DEFAULT_KLAVIYO_LIST_ID } from '@constants';
import getOmsUrl from '@utils/getOmsUrl';
import { newsletterSignupTrack } from '@utils/tracking';

export default function useNewsletter(location: string = '', listId: string = DEFAULT_KLAVIYO_LIST_ID) {
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccessful, setnewsletterSuccessful] = useState(false);

  const addToNewsletter = useCallback(
    async (firebaseAuth: Auth | undefined, email?: string, name?: string) => {
      const formattedEmail = (email || firebaseAuth?.currentUser?.email || '').trim().toLowerCase();
      setNewsletterLoading(true);
      const oms = getOmsUrl();
      newsletterSignupTrack(location);
      const url = `${oms}/klaviyo/${listId}/add`;

      const profile: Record<string, string> = {
        email: formattedEmail,
        consent: 'web'
      };

      if (name) {
        const [firstName, ...lastNameParts] = name.split(' ');

        profile.first_name = firstName;
        profile.last_name = lastNameParts.join(' ');
      }

      const body = JSON.stringify({
        profiles: [profile]
      });

      const options = {
        method: 'POST',
        headers: {
          'api-key': process.env.KLAVIYO_KEY || '',
          'Content-Type': 'application/json'
        },
        body
      };
      try {
        await fetch(url, options);
        setNewsletterLoading(false);
        setnewsletterSuccessful(true);
      } catch (error) {
        Sentry.captureException(error, { tags: { failed: 'addToNewsletter' } });
        setNewsletterLoading(false);
        setnewsletterSuccessful(false);
      }
    },
    [location, listId]
  );

  const removeFromNewsletter = useCallback(
    async (firebaseAuth: Auth | undefined) => {
      setNewsletterLoading(true);
      if (!firebaseAuth) return;
      const oms = getOmsUrl();
      const { currentUser } = firebaseAuth;
      if (!currentUser?.email) return;
      const formattedEmail = currentUser?.email.trim().toLowerCase();
      const body = JSON.stringify({
        profiles: [
          {
            email: formattedEmail,
            consent: 'web'
          }
        ]
      });
      const url = `${oms}/klaviyo/${listId}/remove`;
      const options = {
        method: 'POST',
        headers: {
          'api-key': process.env.KLAVIYO_KEY || '',
          'Content-Type': 'application/json'
        },
        body
      };
      try {
        await fetch(url, options);
        setNewsletterLoading(false);
        setnewsletterSuccessful(true);
      } catch (error) {
        console.error(error);
        setNewsletterLoading(false);
        setnewsletterSuccessful(false);
      }
    },
    [listId]
  );

  return { addToNewsletter, removeFromNewsletter, newsletterLoading, newsletterSuccessful };
}
